// Calendar days in an IANA time zone (Swift `Calendar.startOfDay(for:)` and `date(byAdding: .day…)` with a
// `TimeZone`), built on Intl. Used by the MET Norway daily values, which describe the device's local days.

import type { EpochMs } from '../core/models';

interface LocalParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

function localParts(instant: EpochMs, timeZone: string): LocalParts {
  if (timeZone === 'UTC') {
    const d = new Date(instant);
    return {
      year: d.getUTCFullYear(),
      month: d.getUTCMonth() + 1,
      day: d.getUTCDate(),
      hour: d.getUTCHours(),
      minute: d.getUTCMinutes(),
      second: d.getUTCSeconds(),
    };
  }
  const out: Record<string, number> = {};
  for (const part of formatter(timeZone).formatToParts(new Date(instant))) {
    if (part.type !== 'literal') out[part.type] = Number(part.value);
  }
  const { year, month, day, minute, second } = out;
  return { year, month, day, hour: out.hour % 24, minute, second };
}

/** Offset of the zone from UTC at `instant`, milliseconds (local = UTC + offset). */
function offsetAt(instant: EpochMs, timeZone: string): number {
  const p = localParts(instant, timeZone);
  const whole = Math.floor(instant / 1000) * 1000;
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - whole;
}

/** The instant whose local wall-clock time in `timeZone` is the given local midnight. */
function localMidnight(year: number, month: number, day: number, timeZone: string): EpochMs {
  const wall = Date.UTC(year, month - 1, day);
  let guess = wall - offsetAt(wall, timeZone);
  for (let i = 0; i < 3; i++) {
    const next = wall - offsetAt(guess, timeZone);
    if (next === guess) break;
    guess = next;
  }
  // A DST jump at midnight: the day starts at the first instant whose local date is that day.
  const p = localParts(guess, timeZone);
  if (p.year !== year || p.month !== month || p.day !== day) guess += 3_600_000;
  return guess;
}

/** First instant of the local day containing `instant`. */
export function startOfDay(instant: EpochMs, timeZone: string): EpochMs {
  if (timeZone === 'UTC') return Math.floor(instant / 86_400_000) * 86_400_000;
  const p = localParts(instant, timeZone);
  return localMidnight(p.year, p.month, p.day, timeZone);
}

/** Start of the local day `days` after the local day that starts at `dayStart`. */
export function addDays(dayStart: EpochMs, days: number, timeZone: string): EpochMs {
  if (timeZone === 'UTC') return dayStart + days * 86_400_000;
  const p = localParts(dayStart, timeZone);
  const next = new Date(Date.UTC(p.year, p.month - 1, p.day + days));
  return localMidnight(next.getUTCFullYear(), next.getUTCMonth() + 1, next.getUTCDate(), timeZone);
}

/** The device's time zone (Swift `TimeZone.autoupdatingCurrent`). */
export function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}
